import { Chip, Tooltip } from "@mui/material";
import type { ChipProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { HealthStatus, PremiseHealth } from "@shared/types/foyer";

/** Traffic-light mapping for the rolled-up premise health verdict. */
const STATUS_META: Record<HealthStatus, { color: ChipProps["color"] }> = {
  healthy: { color: "success" },
  warning: { color: "warning" },
  critical: { color: "error" },
  unknown: { color: "default" },
};

/**
 * Green/amber/red premise-health chip. Hovering reveals the underlying reasons
 * (e.g. "2 devices offline", "1 on an old version") so an operator gets the gist
 * without opening the Fleet console.
 */
export default function HealthChip({
  health,
  size = "small",
  testId,
}: {
  health?: PremiseHealth;
  size?: ChipProps["size"];
  testId?: string;
}) {
  const { t } = useTranslation("premises");
  const status = health?.status ?? "unknown";
  const meta = STATUS_META[status];
  const label = t(`healthChip.status.${status}`);
  const reasons = health?.reasons ?? [];
  const tip = reasons.length > 0 ? reasons.join(" · ") : label;
  return (
    <Tooltip title={tip} arrow>
      <Chip
        size={size}
        color={meta.color}
        variant={meta.color === "default" ? "outlined" : "filled"}
        label={label}
        data-testid={testId}
      />
    </Tooltip>
  );
}
